import { BackButton, PageContainer, PageHeader } from "@client/components/ui";
import { Button } from "@client/components/ui/button";
import { UrlMappingPreview } from "@client/components/ui/url-mapping-preview";

import { queryUtils, trpc } from "@client/lib/trpc-client";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DeleteSiteDialog } from "../-components/delete-site-dialog";
import { LlmsFileCard } from "../-components/llms-file-card";
import { SiteActions } from "../-components/site-actions";
import { SiteDetailSkeleton } from "../-components/site-detail-skeleton";
import { SiteGenerationsCard } from "../-components/site-generations-card";
import { SiteStatusCard } from "../-components/site-status-card";

export const Route = createFileRoute("/(protected)/sites/$siteId")({
	component: SiteDetailPage,
	// Warm the cache as soon as the router knows we're heading here. With the
	// router's `defaultPreload: "intent"` this fires on link hover, so the data
	// is usually in place before the component mounts. Not awaited: navigation
	// stays instant and the skeleton covers the gap on a cold cache.
	loader: ({ params }) => {
		// `silentError`: a prefetch failure (e.g. deep link while logged out) must
		// not raise a toast — the component's own error UI owns that.
		void queryUtils.sites.getById.prefetch(
			{ id: params.siteId },
			{ meta: { silentError: true } },
		);
		void queryUtils.sites.getRecentActivities.prefetch(
			{ limit: 50 },
			{ meta: { silentError: true } },
		);
	},
});

function SiteDetailPage() {
	const { siteId } = Route.useParams();
	const navigate = useNavigate();
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

	const utils = trpc.useUtils();

	const site = trpc.sites.getById.useQuery(
		{ id: siteId },
		{
			// Render immediately from whatever the sites list already fetched, then
			// swap in the full record. `placeholderData` (not `initialData`) keeps
			// this out of the cache, so the real fetch still happens.
			placeholderData: () =>
				utils.sites.getRecent
					.getData({ limit: 12, cursor: 0 })
					?.sites.find((s) => s.id === siteId) ??
				utils.sites.list.getData()?.find((s) => s.id === siteId),
			// This query renders its own error card below.
			meta: { silentError: true },
		},
	);

	const deleteMutation = trpc.sites.delete.useMutation({
		onSuccess: () => {
			toast.success("Site deleted successfully");
			// Drop this site's detail cache and refresh every list/count that
			// includes it (sites.list powers useUserLimits, getRecent the grid).
			utils.sites.getById.reset({ id: siteId });
			utils.sites.list.invalidate();
			utils.sites.getRecent.invalidate();
			utils.sites.getCount.invalidate();
			navigate({ to: "/sites" });
		},
		onError: (error) => {
			toast.error(error.message || "Failed to delete site");
			setIsDeleteDialogOpen(false);
		},
	});

	const handleGenerateFile = () => {
		if (!site.data?.url) return;

		// Redirect to options page for existing sites
		navigate({
			to: "/sites/new",
			search: { url: site.data.url, showOptions: true },
		});
	};

	const handleDelete = () => {
		deleteMutation.mutate({ id: siteId });
	};

	if (site.isPending) {
		return (
			<PageContainer maxWidth="wide">
				<BackButton to="/sites" label="Back to sites" />
				<SiteDetailSkeleton />
			</PageContainer>
		);
	}

	if (site.isError) {
		return (
			<PageContainer maxWidth="wide">
				<BackButton to="/sites" label="Back to sites" />

				<div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center dark:border-red-900 dark:bg-red-950/30">
					<h3 className="mb-2 font-semibold text-lg text-red-900 dark:text-red-200">
						Error loading site
					</h3>
					<p className="mb-4 text-red-800 text-sm dark:text-red-300">
						There was a problem loading this site.
					</p>
					<Button onClick={() => site.refetch()} variant="secondary">
						Try Again
					</Button>
				</div>
			</PageContainer>
		);
	}

	// `isPending`/`isError` above leave only the success state, so `site.data` is
	// guaranteed here (placeholder data included).
	const hostname = new URL(site.data.url).hostname;

	return (
		<PageContainer maxWidth="wide">
			<BackButton to="/sites" label="Back to sites" />

			<PageHeader
				title={hostname}
				description={
					<a
						href={site.data.url}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center hover:underline dark:text-gray-300"
					>
						{site.data.url}
						<ExternalLink className="ml-1 h-3 w-3" />
					</a>
				}
			/>

			<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
				<SiteStatusCard
					status={site.data.status}
					updatedAt={site.data.updatedAt}
					createdAt={site.data.createdAt}
				/>
				<SiteGenerationsCard siteId={siteId} siteUrl={site.data.url} />
			</div>

			<div className="mt-6">
				<LlmsFileCard
					siteId={siteId}
					fileTextUrl={site.data.fileTextUrl}
					onGenerate={handleGenerateFile}
					isGenerating={false}
				/>
			</div>

			<div className="mt-6">
				<UrlMappingPreview
					url={site.data.url}
					maxUrls={50}
					siteId={siteId}
					className="mt-0"
				/>
			</div>

			<SiteActions
				hasFile={!!site.data.fileTextUrl}
				isFetching={site.isFetching}
				onGenerate={handleGenerateFile}
				onRefresh={() => site.refetch()}
				onDelete={() => setIsDeleteDialogOpen(true)}
				isGenerating={false}
			/>

			<DeleteSiteDialog
				isOpen={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
				onConfirm={handleDelete}
				isDeleting={deleteMutation.isPending}
			/>
		</PageContainer>
	);
}
